
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Brain, Sparkles, Zap } from "lucide-react";

interface ColoredAISelectorProps {
  selectedAI: "gemini" | "chatgpt" | "claude" | "deepseek";
  onAIChange: (value: "gemini" | "chatgpt" | "claude" | "deepseek") => void;
  disabled?: boolean;
}

const ColoredAISelector: React.FC<ColoredAISelectorProps> = ({ selectedAI, onAIChange, disabled = false }) => {
  const getAIColor = (ai: string) => {
    switch (ai) {
      case 'gemini':
        return {
          bg: 'bg-gradient-to-r from-blue-500 to-purple-600',
          text: 'text-white',
          border: 'border-blue-500',
          hover: 'hover:from-blue-600 hover:to-purple-700'
        };
      case 'chatgpt':
        return {
          bg: 'bg-gradient-to-r from-green-500 to-emerald-600',
          text: 'text-white',
          border: 'border-green-500',
          hover: 'hover:from-green-600 hover:to-emerald-700'
        };
      case 'claude':
        return {
          bg: 'bg-gradient-to-r from-orange-500 to-red-600',
          text: 'text-white',
          border: 'border-orange-500',
          hover: 'hover:from-orange-600 hover:to-red-700'
        };
      case 'deepseek':
        return {
          bg: 'bg-gradient-to-r from-cyan-500 to-blue-600',
          text: 'text-white',
          border: 'border-cyan-500',
          hover: 'hover:from-cyan-600 hover:to-blue-700'
        };
      default:
        return {
          bg: 'bg-gray-500',
          text: 'text-white',
          border: 'border-gray-500',
          hover: 'hover:bg-gray-600'
        };
    }
  };

  const currentColors = getAIColor(selectedAI);

  return (
    <Card className={disabled ? "opacity-50" : ""}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="w-5 h-5" />
          Inteligência Artificial
        </CardTitle>
        <CardDescription className="text-gray-700 dark:text-gray-300 font-medium">
          Escolha qual IA usar para gerar palpites
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Select value={selectedAI} onValueChange={onAIChange} disabled={disabled}>
          <SelectTrigger className={`${currentColors.border} ${currentColors.bg} ${currentColors.text} ${currentColors.hover} transition-all duration-200`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-50 bg-background border border-border shadow-lg">
            <SelectItem value="gemini" className="hover:bg-muted focus:bg-muted text-foreground">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600"></div>
                <Sparkles className="w-4 h-4 text-blue-600" />
                <span className="text-foreground font-medium">Google Gemini</span>
              </div>
            </SelectItem>
            <SelectItem value="chatgpt" className="hover:bg-muted focus:bg-muted text-foreground">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-600"></div>
                <Brain className="w-4 h-4 text-green-600" />
                <span className="text-foreground font-medium">ChatGPT</span>
              </div>
            </SelectItem>
            <SelectItem value="claude" className="hover:bg-muted focus:bg-muted text-foreground">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-orange-500 to-red-600"></div>
                <Zap className="w-4 h-4 text-orange-600" />
                <span className="text-foreground font-medium">Claude.ai</span>
              </div>
            </SelectItem>
            <SelectItem value="deepseek" className="hover:bg-muted focus:bg-muted text-foreground">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600"></div>
                <Brain className="w-4 h-4 text-cyan-600" />
                <span className="text-foreground font-medium">DeepSeek</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </CardContent>
    </Card>
  );
};

export default ColoredAISelector;
